import {
  biffCmd,
  installBanners,
  isBiffLoaded,
  isBiffLoadRequested,
  loadBiff,
  refreshRemountedSlot,
} from '@/app/common/biff/biffLoader.ts';
import { type BIFF_CONFIGURATIONS, POSITIONS } from './typesSearchPage.ts';

const AFS_OPTIONS = { number: 3 };

/** The configuration whose slots this page installed, until a new configuration load starts (see `showRemountedAd`). */
let installedConfiguration: BIFF_CONFIGURATIONS | undefined;

/** AFS containers installed since the current page configuration was requested (see `showRemountedAd`). */
const installedAfsIds = new Set<string>();

/** The results page the user is on, sent to AdSense as `adPage` with every AFS request (see `showAfsPage`). */
let afsPage = 1;

const getAfsParams = (elements: string[]): BiffInstallBannersParams => ({
  elements,
  provider: 'googleAFS',
  options: AFS_OPTIONS,
  pageOptions: { adPage: afsPage },
});

export const loadGPT = ({ configuration }: { configuration: BIFF_CONFIGURATIONS }): void => {
  const { GPT } = POSITIONS[configuration];
  if (GPT.length === 0) return;

  installBanners({ elements: GPT, provider: 'googleDFP' });
};

/**
 * AFS (AdSense for Search) needs the search query enriched with the destination before its slots are
 * installed. The query helpers go through gpt, which only exists once biff has loaded the configuration —
 * `initBiffSearchPage` calls this after that point.
 */
const loadAFS = ({
  configuration,
  destination,
}: {
  configuration: BIFF_CONFIGURATIONS;
  destination?: string;
}): void => {
  const { AFS } = POSITIONS[configuration];
  if (AFS.length === 0) return;

  biffCmd((biff) => {
    biff.Includer.getAsyncSearchEnginesQuery()
      .then((searchQuery) => {
        const words = [searchQuery, destination]
          .filter((part): part is string => !!part)
          .join(' ')
          .split(' ');
        biff.Includer.setSearchEnginesQuery([...new Set(words)].join(' '));
        biff.Includer.installBanners(getAfsParams(AFS));
        AFS.forEach((id) => installedAfsIds.add(id));
      })
      // Without this the rejection is unhandled and the AFS slots are silently left out.
      .catch((error) => {
        console.error('[biff] AFS query failed', { configuration, error });
      });
  });
};

type LoadConfigurationsProps = {
  configuration: BIFF_CONFIGURATIONS;
  destination?: string;
};
export const loadConfigurationsSearchPage = ({ configuration, destination }: LoadConfigurationsProps): void => {
  if (!POSITIONS[configuration]) return;

  installedConfiguration = configuration;
  loadGPT({ configuration });
  loadAFS({ configuration, destination });
};

type InitBiffSearchPageProps = {
  configuration: BIFF_CONFIGURATIONS;
  destination?: string;
  isLoggedIn: boolean;
  /** `window.platformData.length` when the page started, so only this page's visit event is awaited. */
  fromIndex?: number;
  /** Aborts the request when the page goes away, so a load still queued on consent cannot install its slots. */
  signal?: AbortSignal;
  /** The results page the search opened on, sent to AdSense as `adPage`. */
  page?: number;
};

/**
 * Requests the search-page ad configuration and installs its slots once biff has defined them. The biff
 * script is booted page-wide by BiffBootstrap and gates on consent on its own.
 *
 * The slots are registered only after `load()` for this configuration has completed: registered earlier,
 * biff would run the registration against the previous page's slots (a client-side destinations → results
 * or results → detail navigation) and drop every id it does not know yet. The loader keeps one request per
 * configuration until biff loads a different one, so the effects calling this can run as often as they like:
 * the slots are installed once per load(), and a new search after a hotel detail visit loads them again.
 */
export const initBiffSearchPage = ({
  configuration,
  destination,
  isLoggedIn,
  fromIndex,
  signal,
  page = 1,
}: InitBiffSearchPageProps): void => {
  // Kept for a search that reuses the loaded configuration too: its next page change is measured from here.
  afsPage = page;
  if (isBiffLoadRequested(configuration)) return;

  // A new load() starts over: the install that goes with it marks the configuration and its AFS containers again.
  installedConfiguration = undefined;
  installedAfsIds.clear();
  loadBiff({ isLoggedIn, key: configuration, fromIndex, signal }).then((loaded) => {
    if (loaded && !signal?.aborted) loadConfigurationsSearchPage({ configuration, destination });
  });
};

type ShowRemountedAdProps = {
  id: string;
  /** The container now in the document for `id`. */
  node: HTMLElement;
};

/**
 * Called by every search-page ad container when it mounts. The first mount is filled by the install that
 * follows load(); this covers the later ones. A mobile list/map switch remounts the results list, and with it the
 * containers gpt and AdSense had already rendered into, while the slots stay defined and nothing installs
 * them again. gpt slots are refreshed into the new node; an AFS container is installed again, which asks
 * AdSense for a fresh block with the query set at the first install. Before the first install there is
 * nothing to bring back, so a container mounting then is left to it. A container that already holds an ad
 * (one that was hidden and shows now) is left alone too.
 *
 * Back from a hotel detail page the detail configuration is still loaded. gpt keeps the results slots, so only
 * those are refreshed, never a detail slot under the same id (`div-gpt-ad-intrip1`). AFS is left out: biff
 * would install it with the detail configuration and its search query.
 */
export const showRemountedAd = ({ id, node }: ShowRemountedAdProps): void => {
  if (!installedConfiguration || node.childElementCount > 0) return;

  if (refreshRemountedSlot(id, installedConfiguration)) return;

  if (isBiffLoaded(installedConfiguration) && installedAfsIds.has(id)) {
    installBanners(getAfsParams([id]));
  }
};

/**
 * Called once a results page reached without a reload (pager, browser back/forward, a filter taking the list
 * back to page 1) has its products. AdSense counts one request per page: the AFS containers installed with this
 * configuration are installed again with the new page as `adPage` and the query set at the first install, which
 * Google requires to stay the same across pages. Before that install the page is only kept for it; with another
 * configuration loaded (back from a hotel detail page) AFS is left out, as in `showRemountedAd`. So is a container
 * not in the document (the mobile map view unmounts the list): AdSense would be asked for a block with nowhere to
 * render it, and `showRemountedAd` installs it with this page once the list mounts again.
 */
export const showAfsPage = (page: number): void => {
  if (page === afsPage) return;

  afsPage = page;
  if (!installedConfiguration || !isBiffLoaded(installedConfiguration)) return;

  const mountedAfsIds = [...installedAfsIds].filter((id) => document.getElementById(id));
  if (mountedAfsIds.length > 0) installBanners(getAfsParams(mountedAfsIds));
};

export const resetBiffSearchPageForTests = (): void => {
  installedConfiguration = undefined;
  installedAfsIds.clear();
  afsPage = 1;
};
